import React, { useEffect, useState } from 'react';
import type { LicensePlan } from 'edinshall-licensing-shared';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/status';
import { usePortalContext } from '../context/PortalContext';
import { getAccessPathDescription, getAccessPathLabel } from '../utils/accessPath';
import { canDeployToAppCatalog } from '../utils/capabilities';
import { acquireGraphFeatureTokenInteractively } from '../services/graph/interactiveGraphFeatureClient';

type InstancesSummary = {
  totalActive: number;
  totalStale: number;
  needsUpdate: number;
};

type ReportNameConcealmentState = {
  available: boolean;
  active: boolean;
  displayConcealedNames?: boolean;
  effectiveConcealNames: boolean;
  lastSyncedAt?: string;
  lastSyncedBy?: string;
  clientId?: string;
  tokenHeaderName: string;
  permissions: string[];
};

type CommunicationSettingsState = {
  available: boolean;
  active: boolean;
  providerId: 'outlook-mailto' | 'outlook-graph' | 'teams-deeplink' | 'generic';
  icon: 'OutlookLogo' | 'TeamsLogo' | 'Mail';
  effectiveProviderId: 'outlook-mailto' | 'outlook-graph' | 'teams-deeplink' | 'generic';
  graphDraftActive: boolean;
  clientId?: string;
  permissions: string[];
};

const readError = async (response: Response): Promise<string> => {
  const payload = await response.json().catch(() => undefined) as { error?: { message?: string } | string; message?: string } | undefined;
  return typeof payload?.error === 'string'
    ? payload.error
    : payload?.error?.message ?? payload?.message ?? `Request failed (${response.status}).`;
};

type ConsumptionMeter = {
  meterId: 'translation.characters' | 'externalshare.operations' | 'ai.units' | 'support.hours';
  usedRaw: number;
  monthlyIncludedRaw: number;
  onboardingIncludedRaw: number;
  onboardingAvailableRaw: number;
  effectiveIncludedRaw: number;
  availableIncludedRaw: number;
  remainingRaw: number;
  overageRaw: number;
  overageDimensionUnits: number;
};

type ConsumptionView = {
  billingPeriod: { id: string; start: string; end: string };
  plan: LicensePlan;
  onboarding: {
    supportAllowanceHours: number;
    supportAllowanceRemainingBeforePeriod: number;
    windowEndsAt: string | null;
  };
  meters: ConsumptionMeter[];
};

const METER_LABEL: Record<ConsumptionMeter['meterId'], string> = {
  'ai.units': 'AI usage units',
  'externalshare.operations': 'Supplier sharing operations',
  'support.hours': 'Assisted support hours',
  'translation.characters': 'Translation characters (Day 2 reserve)'
};

const formatMeterQuantity = (meterId: ConsumptionMeter['meterId'], value: number): string => {
  if (meterId === 'translation.characters') return Math.round(value).toLocaleString();
  if (meterId === 'support.hours') return `${value.toFixed(2).replace(/\.00$/, '')} h`;
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
};

const Account = (): JSX.Element => {
  const { tenant, products } = usePortalContext();
  const canManageInstallations = canDeployToAppCatalog(tenant);
  const canManagePrivacy = tenant?.capabilities?.canManagePermissions === true;
  const [instancesSummary, setInstancesSummary] = useState<InstancesSummary | null>(null);
  const [instancesLoading, setInstancesLoading] = useState<boolean>(true);
  const [instancesDegraded, setInstancesDegraded] = useState<boolean>(false);
  const [instancesPermissionBlocked, setInstancesPermissionBlocked] = useState<boolean>(false);
  const [privacy, setPrivacy] = useState<ReportNameConcealmentState | null>(null);
  const [privacyLoading, setPrivacyLoading] = useState<boolean>(false);
  const [privacySaving, setPrivacySaving] = useState<boolean>(false);
  const [privacyError, setPrivacyError] = useState<string>();
  const [communication, setCommunication] = useState<CommunicationSettingsState | null>(null);
  const [communicationLoading, setCommunicationLoading] = useState(false);
  const [communicationSaving, setCommunicationSaving] = useState(false);
  const [communicationError, setCommunicationError] = useState<string>();
  const [consumption, setConsumption] = useState<ConsumptionView | null>(null);
  const [consumptionLoading, setConsumptionLoading] = useState<boolean>(true);
  const [consumptionError, setConsumptionError] = useState<string>();

  useEffect(() => {
    if (!canManageInstallations) {
      setInstancesLoading(false);
      setInstancesSummary(null);
      setInstancesDegraded(false);
      setInstancesPermissionBlocked(true);
      return;
    }

    const loadInstances = async (): Promise<void> => {
      setInstancesLoading(true);
      try {
        const response = await fetch('/api/instances');
        if (!response.ok) {
          if (response.status === 403) {
            setInstancesPermissionBlocked(true);
          }
          setInstancesSummary(null);
          setInstancesDegraded(false);
          return;
        }
        const data = (await response.json()) as { summary?: InstancesSummary; degraded?: boolean };
        setInstancesSummary(data.summary ?? null);
        setInstancesDegraded(data.degraded === true);
        setInstancesPermissionBlocked(false);
      } catch {
        setInstancesSummary(null);
        setInstancesDegraded(false);
      } finally {
        setInstancesLoading(false);
      }
    };

    void loadInstances();
  }, [canManageInstallations]);

  const loadPrivacy = async (): Promise<void> => {
    if (!canManagePrivacy) return;
    setPrivacyLoading(true);
    setPrivacyError(undefined);
    try {
      const response = await fetch('/api/account/report-name-concealment');
      if (!response.ok) throw new Error(await readError(response));
      setPrivacy(await response.json() as ReportNameConcealmentState);
    } catch (error) {
      setPrivacyError((error as Error).message);
    } finally {
      setPrivacyLoading(false);
    }
  };

  useEffect(() => {
    void loadPrivacy();
  }, [canManagePrivacy]);

  const loadCommunication = async (): Promise<void> => {
    if (!canManagePrivacy) return;
    setCommunicationLoading(true);
    setCommunicationError(undefined);
    try {
      const response = await fetch('/api/account/communication');
      if (!response.ok) throw new Error(await readError(response));
      setCommunication(await response.json() as CommunicationSettingsState);
    } catch (error) {
      setCommunicationError((error as Error).message);
    } finally {
      setCommunicationLoading(false);
    }
  };

  useEffect(() => {
    void loadCommunication();
  }, [canManagePrivacy]);

  const saveCommunication = async (): Promise<void> => {
    if (!communication) return;
    setCommunicationSaving(true);
    setCommunicationError(undefined);
    try {
      const response = await fetch('/api/account/communication', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          active: communication.providerId === 'outlook-graph' ? communication.active : false,
          providerId: communication.providerId,
          icon: communication.icon
        })
      });
      if (!response.ok) throw new Error(await readError(response));
      await loadCommunication();
    } catch (error) {
      setCommunicationError((error as Error).message);
    } finally {
      setCommunicationSaving(false);
    }
  };

  const synchronizePrivacy = async (): Promise<void> => {
    if (!privacy?.available || !privacy.clientId || !tenant?.tenantId) return;
    setPrivacySaving(true);
    setPrivacyError(undefined);
    try {
      const token = await acquireGraphFeatureTokenInteractively({
        clientId: privacy.clientId,
        tenantId: tenant.tenantId,
        scopes: ['ReportSettings.Read.All']
      });
      const response = await fetch('/api/account/report-name-concealment', {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          [privacy.tokenHeaderName]: token
        },
        body: JSON.stringify({ active: true })
      });
      if (!response.ok) throw new Error(await readError(response));
      await loadPrivacy();
    } catch (error) {
      setPrivacyError((error as Error).message);
    } finally {
      setPrivacySaving(false);
    }
  };

  const disablePrivacy = async (): Promise<void> => {
    setPrivacySaving(true);
    setPrivacyError(undefined);
    try {
      const response = await fetch('/api/account/report-name-concealment', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ active: false })
      });
      if (!response.ok) throw new Error(await readError(response));
      await loadPrivacy();
    } catch (error) {
      setPrivacyError((error as Error).message);
    } finally {
      setPrivacySaving(false);
    }
  };

  useEffect(() => {
    const loadConsumption = async (): Promise<void> => {
      setConsumptionLoading(true);
      setConsumptionError(undefined);
      try {
        const response = await fetch('/api/account/consumption');
        if (!response.ok) {
          throw new Error(`Consumption API returned ${response.status}`);
        }
        setConsumption((await response.json()) as ConsumptionView);
      } catch {
        setConsumption(null);
        setConsumptionError('Current consumption is temporarily unavailable.');
      } finally {
        setConsumptionLoading(false);
      }
    };
    void loadConsumption();
  }, [tenant?.tenantId]);

  return (
    <div>
      <h1 className="section-title">Account &amp; Subscription</h1>
      <p className="muted">Billing and subscription changes are managed via Microsoft Marketplace.</p>

      <div className="card">
        <h3>{tenant?.tenantName}</h3>
        <p className="muted">{tenant?.userEmail}</p>
        <div className="badge-subtle" style={{ margin: '8px 0' }}>
          Subscription source: {tenant?.subscriptionSource ?? 'Microsoft Marketplace'}
        </div>
        <div className="badge-subtle" style={{ margin: '8px 0' }}>
          Access path: {getAccessPathLabel(tenant)}
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>{getAccessPathDescription(tenant)}</p>
      </div>

      <h3 style={{ marginTop: '16px' }}>Products owned</h3>
      {products.length === 0 && (
        <EmptyState
          message={
            <>
              No products are assigned to this tenant yet. Entitlements arrive from Microsoft Marketplace; if
              you expected one here, <a href="/support">contact support</a>.
            </>
          }
        />
      )}
      <div className="grid columns-3">
        {products.map((product) => (
          <div key={product.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>{product.name}</strong>
              <StatusBadge status={product.status} />
            </div>
          </div>
        ))}
      </div>

      <h3 style={{ marginTop: '16px' }}>Current consumption</h3>
      <p className="muted">
        Month-to-date usage against the inclusion for your {consumption?.plan ?? tenant?.planCode ?? 'current'} plan.
        Marketplace billing remains subject to the feature and owner-controlled emission switches; a displayed overage is not itself a charge.
      </p>
      {consumptionLoading && <div className="badge-subtle">Loading consumption…</div>}
      {!consumptionLoading && consumptionError && (
        <div className="card"><p className="muted" style={{ margin: 0 }}>{consumptionError}</p></div>
      )}
      {!consumptionLoading && consumption && (
        <div className="grid columns-2">
          {consumption.meters.map((meter) => (
            <div key={meter.meterId} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                <strong>{METER_LABEL[meter.meterId]}</strong>
                <span className={meter.overageRaw > 0 ? 'pill warning' : 'badge-subtle'}>
                  {meter.overageRaw > 0 ? 'Above inclusion' : 'Within inclusion'}
                </span>
              </div>
              <div style={{ marginTop: '8px' }}>
                <strong>{formatMeterQuantity(meter.meterId, meter.usedRaw)}</strong>
                <span className="muted"> used of {formatMeterQuantity(meter.meterId, meter.availableIncludedRaw)} currently available</span>
              </div>
              <div className="muted" style={{ marginTop: '4px' }}>
                Monthly plan inclusion: {formatMeterQuantity(meter.meterId, meter.monthlyIncludedRaw)}
                {meter.meterId === 'support.hours' && meter.onboardingAvailableRaw > 0
                  ? ` · onboarding allowance available: ${formatMeterQuantity(meter.meterId, meter.onboardingAvailableRaw)}`
                  : ''}
              </div>
              <div className="muted" style={{ marginTop: '4px' }}>
                Remaining: {formatMeterQuantity(meter.meterId, meter.remainingRaw)}
                {' · Projected overage: '}
                {formatMeterQuantity(meter.meterId, meter.overageRaw)}
              </div>
              {meter.meterId === 'translation.characters' && (
                <div className="muted" style={{ marginTop: '6px' }}>
                  The meter identifier is reserved, but Translation is not a Day 1 feature and records no usage.
                </div>
              )}
            </div>
          ))}
          <div className="muted" style={{ gridColumn: '1 / -1' }}>
            Billing period {consumption.billingPeriod.id}.
            {consumption.onboarding.windowEndsAt
              ? ` The one-time assisted-support onboarding window ends ${new Date(consumption.onboarding.windowEndsAt).toLocaleDateString()}.`
              : ''}
          </div>
        </div>
      )}

      <h3 style={{ marginTop: '16px' }}>Installations</h3>
      {instancesLoading ? (
        <div className="badge-subtle">Loading installations…</div>
      ) : instancesPermissionBlocked ? (
        <div className="card">
          <p className="muted">
            Installation management is available to customer deployment administrators.
            Ask a tenant deployment admin to grant the deployment capability for this account.
          </p>
        </div>
      ) : instancesDegraded ? (
        <div className="card">
          <p className="muted">Installations are temporarily unavailable from inventory storage.</p>
        </div>
      ) : instancesSummary ? (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div>
              <strong>{instancesSummary.totalActive} active</strong>
              {instancesSummary.totalStale > 0 && (
                <span className="muted"> · {instancesSummary.totalStale} stale</span>
              )}
              {instancesSummary.needsUpdate > 0 && (
                <span className="pill warning" style={{ marginLeft: '8px' }}>
                  {instancesSummary.needsUpdate} needs update
                </span>
              )}
            </div>
            <a href="/instances" className="btn secondary">View all</a>
          </div>
        </div>
      ) : (
        <div className="card">
          <p className="muted">No installations detected yet. Deploy the web part to a SharePoint site to see it here.</p>
        </div>
      )}

      <h3 style={{ marginTop: '16px' }}>Record communication</h3>
      {!canManagePrivacy ? (
        <div className="card">
          <p className="muted">A Portal Client Administrator can manage record communication.</p>
        </div>
      ) : communicationLoading && !communication ? (
        <div className="badge-subtle">Loading communication settings…</div>
      ) : (
        <div className="card">
          <div style={{ display: 'grid', gap: '12px', maxWidth: '820px' }}>
            <div>
              <strong>Default communication provider</strong>
              <p className="muted">Choose the compose experience shown from Audits, Findings and Actions. Edinshall only prepares a draft; the user reviews, edits and sends it.</p>
              <select
                aria-label="Default communication provider"
                value={communication?.providerId ?? 'outlook-mailto'}
                disabled={!communication || communicationSaving}
                onChange={(event) => setCommunication(current => current ? {
                  ...current,
                  providerId: event.target.value as CommunicationSettingsState['providerId'],
                  active: event.target.value === 'outlook-graph' ? current.active : false
                } : current)}
              >
                <option value="outlook-mailto">Outlook email (standard formatting)</option>
                <option value="outlook-graph">Outlook email (rich formatting and attachments)</option>
                <option value="teams-deeplink">Microsoft Teams</option>
                <option value="generic">Default email application</option>
              </select>
            </div>
            <div>
              <strong>Action icon</strong>
              <select
                aria-label="Action icon"
                value={communication?.icon ?? 'OutlookLogo'}
                disabled={!communication || communicationSaving}
                onChange={(event) => setCommunication(current => current ? {
                  ...current,
                  icon: event.target.value as CommunicationSettingsState['icon']
                } : current)}
              >
                <option value="OutlookLogo">Outlook</option>
                <option value="TeamsLogo">Teams</option>
                <option value="Mail">Email</option>
              </select>
            </div>
            {communication?.providerId === 'outlook-graph' && (
              <div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={communication.active}
                    disabled={communicationSaving || !communication.available}
                    onChange={(event) => setCommunication(current => current ? { ...current, active: event.target.checked } : current)}
                  />
                  <span>
                    <strong>Use Microsoft Graph for rich Outlook email formatting (drafts with attachments)</strong>
                    <span className="muted" style={{ display: 'block' }}>
                      Off by default. This asks each user to consent to delegated <code>Mail.ReadWrite</code> for the dedicated Edinshall communication feature. Your IT team may need to approve it. The permission lets the feature create a draft in that user's own mailbox; Edinshall never sends the message.
                    </span>
                  </span>
                </label>
                {!communication.available && (
                  <p className="muted">Rich Outlook drafts are not currently made available by the Edinshall operator. Standard Outlook drafts remain available.</p>
                )}
                {communication.clientId && tenant?.tenantId && (
                  <p className="muted">
                    If your tenant blocks user consent, ask IT to open{' '}
                    <a
                      href={`https://login.microsoftonline.com/${encodeURIComponent(tenant.tenantId)}/adminconsent?client_id=${encodeURIComponent(communication.clientId)}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth-redirect.html`)}`}
                      target="_blank"
                      rel="noreferrer"
                    >Microsoft administrator approval</a>.
                  </p>
                )}
              </div>
            )}
            {communication?.graphDraftActive && <span className="pill success">Rich Outlook drafts active</span>}
            {communicationError && <p className="error-text">{communicationError}</p>}
            <div>
              <button className="btn primary" type="button" disabled={!communication || communicationSaving} onClick={() => void saveCommunication()}>
                {communicationSaving ? 'Saving…' : 'Save communication settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: '16px' }}>Report privacy</h3>
      {!canManagePrivacy ? (
        <div className="card">
          <p className="muted">A customer permissions administrator can manage optional report privacy.</p>
        </div>
      ) : privacyLoading && !privacy ? (
        <div className="badge-subtle">Loading report privacy…</div>
      ) : (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ maxWidth: '760px' }}>
              <strong>Honour Microsoft 365 concealed names in Edinshall reports</strong>
              <p className="muted">
                Optional and off by default. When enabled, Edinshall reads your Microsoft 365 report setting and conceals people in Audit packs, reporting feeds and Audit History PDF exports when Microsoft 365 says names should be concealed. Operational Audit records and editing screens remain named for accountability.
              </p>
              <p className="muted">
                This requires Microsoft Graph <code>ReportSettings.Read.All</code>. Microsoft requires administrator consent, and the person connecting may need an appropriate Entra administrator role. Your IT team may need to approve it. Free and core Edinshall use do not require this permission unless you enable this feature.
              </p>
              {privacy?.clientId && tenant?.tenantId && (
                <p className="muted">
                  <a
                    href={`https://login.microsoftonline.com/${encodeURIComponent(tenant.tenantId)}/adminconsent?client_id=${encodeURIComponent(privacy.clientId)}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth-redirect.html`)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Microsoft administrator approval
                  </a>{' '}
                  to give your IT administrator the exact feature-specific consent request.
                </p>
              )}
              {privacy?.active && (
                <>
                  <p>
                    <span className={`pill ${privacy.effectiveConcealNames ? 'success' : 'warning'}`}>
                      {privacy.effectiveConcealNames ? 'Names concealed in reports' : 'Names currently visible in reports'}
                    </span>
                    {privacy.lastSyncedAt && (
                      <span className="muted"> · checked {new Date(privacy.lastSyncedAt).toLocaleString()}</span>
                    )}
                  </p>
                  {privacy.effectiveConcealNames && (
                    <p className="muted">Regenerate any stored detailed reporting feeds to remove names from the stored JSON as well as from Edinshall's rendered view.</p>
                  )}
                </>
              )}
              {!privacy?.available && (
                <p className="muted">This optional feature has not yet been configured for this Edinshall environment.</p>
              )}
              {privacyError && <p className="error-text">{privacyError}</p>}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {privacy?.active && (
                <button className="btn secondary" type="button" disabled={privacySaving} onClick={() => void disablePrivacy()}>
                  Stop honouring setting
                </button>
              )}
              <button
                className="btn primary"
                type="button"
                disabled={privacySaving || !privacy?.available}
                onClick={() => void synchronizePrivacy()}
              >
                {privacySaving ? 'Connecting…' : privacy?.active ? 'Refresh from Microsoft 365' : 'Enable and check Microsoft 365'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;

